import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import PlotCard from "@/components/feature/PlotCard";
import { useFavorites } from "@/hooks/useFavorites";
import { episodes } from "@/mocks/episodes";
import { plots } from "@/mocks/plots";

export default function EpisodeDetail() {
  const { id } = useParams<{ id: string }>();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [justToggled, setJustToggled] = useState(false);

  const index = episodes.findIndex((item) => item.id === id);
  const episode = index >= 0 ? episodes[index] : undefined;

  useEffect(() => {
    setJustToggled(false);
  }, [id]);

  if (!episode) {
    return (
      <div className="mx-auto max-w-site px-4 py-20 md:px-6">
        <EmptyState
          title="不存在此剧集"
          description="你访问的剧集信息不存在或链接已失效，请返回剧集列表重新选择。"
          secondaryLabel="返回剧集列表"
          secondaryLink="/category/episodes"
        />
      </div>
    );
  }

  const prev = index > 0 ? episodes[index - 1] : undefined;
  const next = index < episodes.length - 1 ? episodes[index + 1] : undefined;
  const relatedPlots = plots.slice(0, 3);
  const favorited = isFavorite(episode.id);

  return (
    <>
      <PageMeta
        title={`第${episode.ep}集 ${episode.title} - 年轻的母亲1 - 年轻的母亲1官网`}
        description={`《年轻的母亲1》第${episode.ep}集「${episode.title}」剧情介绍与看点：${episode.summary.slice(0, 60)}`}
        keywords={`年轻的母亲1第${episode.ep}集,年轻的母亲1 ${episode.title},年轻的母亲1分集剧情`}
      />

      <div className="mx-auto max-w-site px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "剧集", to: "/category/episodes" }, { label: `第${episode.ep}集` }]} />

        <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div>
            <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              第{episode.ep}集 · {episode.title}
            </h1>

            <div className="relative mt-5 w-full aspect-video overflow-hidden rounded-lg bg-primary-950">
              <img
                src={episode.cover}
                alt={`第${episode.ep}集 ${episode.title} 剧照`}
                title={`年轻的母亲1 第${episode.ep}集 ${episode.title}`}
                className="h-full w-full object-cover object-top opacity-90"
              />
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-primary-950/45">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/90 text-3xl text-primary-600">
                  <i className="ri-play-fill" />
                </span>
                <p className="mt-4 rounded-full bg-primary-950/70 px-4 py-1.5 text-xs text-background-100">
                  <i className="ri-information-line mr-1" />
                  本站仅展示剧集信息，播放功能为界面演示，不支持真实播放
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1">
                <i className="ri-calendar-line text-primary-500" />
                {episode.airDate}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1">
                <i className="ri-time-line text-primary-500" />
                {episode.duration}
              </span>
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-3 py-1">
                <i className="ri-fire-line text-accent-500" />
                {episode.views} 播放
              </span>
              <span className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-secondary-900">
                {episode.status}
              </span>
              <button
                type="button"
                onClick={() => {
                  const now = toggleFavorite(episode.id);
                  setJustToggled(true);
                  if (!now) setJustToggled(false);
                }}
                className={`flex items-center gap-1 rounded-full px-4 py-1 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-accent-500 text-background-50"
                    : "border border-background-200 bg-background-50 text-foreground-600 hover:border-accent-400 hover:text-accent-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏本集"}
              </button>
            </div>
            {justToggled ? (
              <p className="mt-2 text-xs text-accent-600">
                <i className="ri-check-line mr-1" />
                收藏状态已更新，已保存在本设备（刷新后依然保留）
              </p>
            ) : null}

            <section className="mt-8">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                剧集简介
              </h2>
              <p className="mt-3 text-sm leading-loose text-foreground-700">{episode.summary}</p>
            </section>

            <section className="mt-8">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                关键剧情摘要
              </h2>
              <ul className="mt-3 space-y-2">
                {episode.highlights.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-2 rounded-md border border-background-200 bg-background-50 p-3 text-sm text-foreground-700"
                  >
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground-950">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                相关剧情解析
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {relatedPlots.map((plot) => (
                  <PlotCard key={plot.id} plot={plot} />
                ))}
              </div>
            </section>
          </div>

          <aside className="space-y-6">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-base font-semibold text-foreground-950">剧集导航</h3>
              <div className="mt-4 space-y-3">
                {prev ? (
                  <Link
                    to={`/detail/episode/${prev.id}`}
                    className="group flex items-center gap-3 rounded-md border border-background-200 p-3 transition-colors hover:border-primary-300"
                  >
                    <i className="ri-arrow-left-line text-primary-500" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-xs text-foreground-500">上一集 · 第{prev.ep}集</span>
                      <span className="block truncate text-sm font-medium text-foreground-800 group-hover:text-primary-600">
                        {prev.title}
                      </span>
                    </span>
                  </Link>
                ) : (
                  <p className="rounded-md border border-dashed border-background-200 p-3 text-xs text-foreground-400">
                    已经是第一集
                  </p>
                )}
                {next ? (
                  <Link
                    to={`/detail/episode/${next.id}`}
                    className="group flex items-center gap-3 rounded-md border border-background-200 p-3 transition-colors hover:border-primary-300"
                  >
                    <span className="min-w-0 flex-1 text-right">
                      <span className="block text-xs text-foreground-500">下一集 · 第{next.ep}集</span>
                      <span className="block truncate text-sm font-medium text-foreground-800 group-hover:text-primary-600">
                        {next.title}
                      </span>
                    </span>
                    <i className="ri-arrow-right-line text-primary-500" />
                  </Link>
                ) : (
                  <p className="rounded-md border border-dashed border-background-200 p-3 text-xs text-foreground-400">
                    已经是最后一集
                  </p>
                )}
              </div>
              <Link
                to="/category/episodes"
                className="mt-4 flex items-center justify-center gap-1 rounded-md bg-background-100 py-2 text-xs font-medium text-foreground-600 transition-colors hover:bg-background-200"
              >
                返回全部剧集
                <i className="ri-arrow-right-s-line" />
              </Link>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="text-base font-semibold text-foreground-950">本集数据</h3>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">更新时间</dt>
                  <dd className="text-foreground-800">{episode.airDate}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">单集时长</dt>
                  <dd className="text-foreground-800">{episode.duration}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">播放热度</dt>
                  <dd className="text-foreground-800">{episode.views}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-foreground-500">更新状态</dt>
                  <dd className="text-foreground-800">{episode.status}</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}